/**
 * Which Stripe sessions have already been reported, so one is never counted twice as its id
 * reappears on later URLs.
 *
 * Kept per channel because the two gate differently — the dataLayer push happens for everyone, the
 * forward to the GTM server container only with marketing consent.
 *
 * Nothing reaches the device without the channel's consent. Without it the in-memory half still
 * dedupes within the page, and a duplicate that survives a reload is moot because the tags never
 * fire either.
 *
 * A convenience, not the authority: it cannot see another tab or device. crm-service holds the real
 * ledger for the marketing channel.
 */
import { hasAnalyticsConsent, hasMarketingConsent } from '@/lib/consent'

export type PurchaseChannel = 'dataLayer' | 'marketing'

const STORAGE_KEY_BY_CHANNEL: Record<PurchaseChannel, string> = {
  dataLayer: 'zoe.trackedPurchaseSessions',
  marketing: 'zoe.trackedMarketingPurchases',
}

// A channel persists to sessionStorage only with its own consent; without it dedup is in-memory only.
const CONSENT_BY_CHANNEL: Record<PurchaseChannel, () => boolean> = {
  dataLayer: hasAnalyticsConsent,
  marketing: hasMarketingConsent,
}

// Cross-sell chains are two or three sessions; the cap is for repeated buying in one visit.
const MAX_TRACKED_IDS = 20

const memoryTracked: Record<PurchaseChannel, Set<string>> = {
  dataLayer: new Set(),
  marketing: new Set(),
}

const readStored = (channel: PurchaseChannel): string[] => {
  if (typeof window === 'undefined' || !CONSENT_BY_CHANNEL[channel]()) return []
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY_BY_CHANNEL[channel])
    if (!raw) return []
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed.filter((id): id is string => typeof id === 'string')
  } catch {
    return []
  }
}

const writeStored = (channel: PurchaseChannel, ids: string[]): void => {
  if (typeof window === 'undefined' || !CONSENT_BY_CHANNEL[channel]()) return
  try {
    window.sessionStorage.setItem(STORAGE_KEY_BY_CHANNEL[channel], JSON.stringify(ids.slice(-MAX_TRACKED_IDS)))
  } catch {
    // Private modes and storage-full throw; the in-memory mirror still dedupes within the page.
  }
}

/** True when this Stripe session has already been reported on this channel. */
export const hasTrackedPurchase = (sessionId: string, channel: PurchaseChannel = 'dataLayer'): boolean =>
  memoryTracked[channel].has(sessionId) || readStored(channel).includes(sessionId)

/** Records that this Stripe session has been reported on this channel. */
export const markPurchaseTracked = (sessionId: string, channel: PurchaseChannel = 'dataLayer'): void => {
  memoryTracked[channel].add(sessionId)
  const stored = readStored(channel)
  if (stored.includes(sessionId)) return
  writeStored(channel, [...stored, sessionId])
}

/** Undoes markPurchaseTracked so a failed send can be retried on a later page or reload. */
export const unmarkPurchaseTracked = (sessionId: string, channel: PurchaseChannel = 'dataLayer'): void => {
  memoryTracked[channel].delete(sessionId)
  const stored = readStored(channel)
  if (!stored.includes(sessionId)) return
  writeStored(
    channel,
    stored.filter((id) => id !== sessionId),
  )
}
